import { isBlocksStructure, renderBlocksEmail } from './email-blocks';

// Generate HTML from email builder template structure
// Brand border radius is stored as a number of pixels; older values may already include a unit
const toPx = (value, fallback = '4px') => {
  if (value === undefined || value === null || String(value).trim() === '') return fallback;
  return /^\d+(\.\d+)?$/.test(String(value).trim()) ? `${String(value).trim()}px` : String(value).trim();
};

const generateHTMLFromTemplate = (template, brand, creative, lang) => {
  // Block-based templates (one <tr> per block) - see email-blocks.js
  const blocksStructure = isBlocksStructure(template) ? template : template?.structure;
  if (isBlocksStructure(blocksStructure)) {
    const langData = creative?.langs?.find(l => l.languageName === lang) || creative?.langs?.[0] || {};
    const logoLink = brand?.logoLink
      || brand?.langs?.find(l => l.languageName === lang)?.logoLink
      || brand?.langs?.[0]?.logoLink;
    return renderBlocksEmail(blocksStructure, { ...brand, logoLink }, langData);
  }

  // Support both old format (structure only) and new format (full template object)
  const structure = Array.isArray(template) ? template : template?.structure;
  
  if (!structure || !Array.isArray(structure)) {
    return '<p>No template structure found</p>';
  }

  const {
    frameColor = brand?.frameColor || '#3cbec6',
    backgroundColor = brand?.backgroundColor || '#ffffff',
    buttonGenralColor = brand?.buttonGenralColor || '#3cbec6',
    buttonTextColor = brand?.buttonTextColor || '#ffffff',
    logo = brand?.logo || '',
    disclaimer_marketing = brand?.disclaimer_marketing || '',
    smalldisclaimer = brand?.smalldisclaimer || '',
    viewInBrowserURL = brand?.viewInBrowserURL || '#',
    borderRadius = toPx(brand?.borderRadius)
  } = brand || {};
  
  // Get logoLink from langs array (it's language-specific in the brand model)
  const logoLink = brand?.langs?.[0]?.logoLink || brand?.logoLink || '#';
  
  const langData = creative.langs?.find(l => l.languageName === lang) || {};
  
  const {
    headline = '',
    body = '',
    bodyBtnName = '',
    bodyBtnLink = '',
    bodyImage = '',
    bodyImageLink = '',
    additionalText = '',
    additionalTextUnderBTN = ''
  } = langData;

  let bodyContent = '';

  structure.forEach(row => {
    if (row.columns.length > 1) {
      bodyContent += `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;"><tr>`;
      row.columns.forEach(column => {
        const columnWidth = parseInt(column.width);
        bodyContent += `<td width="${columnWidth}%" style="vertical-align:top;padding:0 10px;">`;
        column.elements.forEach(element => {
          bodyContent += generateElementHTML(element, {
            headline,
            body,
            bodyBtnName,
            bodyBtnLink,
            bodyImage,
            bodyImageLink,
            additionalText,
            additionalTextUnderBTN,
            buttonGenralColor,
            buttonTextColor,
            borderRadius,
            smalldisclaimer,
            disclaimer_marketing,
            brandLogo: logo,
            brandLogoLink: logoLink
          });
        });
        bodyContent += `</td>`;
      });
      bodyContent += `</tr></table>`;
    } else {
      row.columns.forEach(column => {
        column.elements.forEach(element => {
          bodyContent += generateElementHTML(element, {
            headline,
            body,
            bodyBtnName,
            bodyBtnLink,
            bodyImage,
            bodyImageLink,
            additionalText,
            additionalTextUnderBTN,
            buttonGenralColor,
            buttonTextColor,
            borderRadius,
            smalldisclaimer,
            disclaimer_marketing,
            brandLogo: logo,
            brandLogoLink: logoLink
          });
        });
      });
    }
  });

  // Wrap in email template
  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${creative.title || 'Email'}</title>
    </head>
    <body style="margin:0;padding:0;font-family:Arial,sans-serif;background-color:#f4f4f4;">
      <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f4f4;">
        <tr>
          <td align="center" style="padding:20px 0;">
            <table width="600" cellpadding="0" cellspacing="0" border="0" style="background-color:${backgroundColor};border:2px solid ${frameColor};border-radius:8px;">
              <tr>
                <td style="padding:20px;">
                  ${bodyContent}
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </body>
    </html>
  `;
};

const generateElementHTML = (element, data) => {
  const content = element.content;
  
  switch (element.type) {
    case 'text':
      // Auto-populate text based on content or use creative data
      let text = content.text || '';
      
      // If text is empty or contains only default, use creative data
      if (!text || text === 'Enter your text here...') {
        text = data.body || '';
      }
      
      // Replace placeholders - support both {{}} and {} formats
      text = text.replace(/\{\{headline\}\}/gi, data.headline || '');
      text = text.replace(/\{headline\}/gi, data.headline || '');
      text = text.replace(/\{\{body\}\}/gi, data.body || '');
      text = text.replace(/\{body\}/gi, data.body || '');
      text = text.replace(/\{\{additionalText\}\}/gi, data.additionalText || '');
      text = text.replace(/\{additionalText\}/gi, data.additionalText || '');
      text = text.replace(/\{\{additionalTextUnderBTN\}\}/gi, data.additionalTextUnderBTN || '');
      text = text.replace(/\{additionalTextUnderBTN\}/gi, data.additionalTextUnderBTN || '');
      text = text.replace(/\{\{bodyImage\}\}/gi, data.bodyImage || '');
      text = text.replace(/\{bodyImage\}/gi, data.bodyImage || '');
      text = text.replace(/\{\{bodyImageLink\}\}/gi, data.bodyImageLink || '');
      text = text.replace(/\{bodyImageLink\}/gi, data.bodyImageLink || '');
      
      // Brand placeholders
      text = text.replace(/\{\{brand\.logo\}\}/gi, data.brandLogo || '');
      text = text.replace(/\{brand\.logo\}/gi, data.brandLogo || '');
      text = text.replace(/\{\{brand\.logoLink\}\}/gi, data.brandLogoLink || '');
      text = text.replace(/\{brand\.logoLink\}/gi, data.brandLogoLink || '');
      
      // Brand snippets placeholders
      text = text.replace(/\{\{snippets\.footer\}\}/gi, data.smalldisclaimer || '');
      text = text.replace(/\{snippets\.footer\}/gi, data.smalldisclaimer || '');
      text = text.replace(/\{\{snippets\.disclaimer\}\}/gi, data.disclaimer_marketing || '');
      text = text.replace(/\{snippets\.disclaimer\}/gi, data.disclaimer_marketing || '');
      
      return `<div style="margin-bottom:15px;"><p style="text-align:${content.align};font-size:${content.fontSize};color:${content.color};margin:0;line-height:1.6;">${text}</p></div>`;
    
    case 'image':
    case 'gif':
      // Use bodyImage from creative if element image is empty or is placeholder
      let imgSrc = content.src || '';
      
      // Replace placeholders
      imgSrc = imgSrc.replace(/\{\{bodyImage\}\}/gi, data.bodyImage || '');
      imgSrc = imgSrc.replace(/\{bodyImage\}/gi, data.bodyImage || '');
      imgSrc = imgSrc.replace(/\{\{brand\.logo\}\}/gi, data.brandLogo || '');
      imgSrc = imgSrc.replace(/\{brand\.logo\}/gi, data.brandLogo || '');
      
      // If empty after placeholder replacement, don't render
      if (!imgSrc) return '';
      
      let imgLink = content.link || '';
      imgLink = imgLink.replace(/\{\{bodyImageLink\}\}/gi, data.bodyImageLink || '');
      imgLink = imgLink.replace(/\{bodyImageLink\}/gi, data.bodyImageLink || '');
      imgLink = imgLink.replace(/\{\{brand\.logoLink\}\}/gi, data.brandLogoLink || '');
      imgLink = imgLink.replace(/\{brand\.logoLink\}/gi, data.brandLogoLink || '');
      
      if (!imgLink && data.bodyImageLink) {
        imgLink = data.bodyImageLink;
      }
      
      return `<div style="text-align:${content.align};margin-bottom:15px;">${imgLink ? `<a href="${imgLink}">` : ''}<img src="${imgSrc}" alt="${content.alt || 'Image'}" style="max-width:100%;height:auto;" />${imgLink ? '</a>' : ''}</div>`;
    
    case 'button':
      // Auto-use button data from creative
      let btnText = content.text || 'Click Here';
      let btnLink = content.link || '#';
      
      // Replace placeholders in button text and link
      btnText = btnText.replace(/\{\{bodyBtnName\}\}/gi, data.bodyBtnName || '');
      btnText = btnText.replace(/\{bodyBtnName\}/gi, data.bodyBtnName || '');
      btnLink = btnLink.replace(/\{\{bodyBtnLink\}\}/gi, data.bodyBtnLink || '');
      btnLink = btnLink.replace(/\{bodyBtnLink\}/gi, data.bodyBtnLink || '');
      
      // If still default values and creative has data, use creative data
      if (btnText === 'Click Here' && data.bodyBtnName) {
        btnText = data.bodyBtnName;
      }
      if (btnLink === '#' && data.bodyBtnLink) {
        btnLink = data.bodyBtnLink;
      }
      
      // Handle button colors with placeholder support
      let btnBg = content.bgColor || '';
      let btnColor = content.textColor || '';
      
      // Replace brand color placeholders
      btnBg = btnBg.replace(/\{\{brand\.buttonColor\}\}/gi, data.buttonGenralColor || '');
      btnBg = btnBg.replace(/\{brand\.buttonColor\}/gi, data.buttonGenralColor || '');
      btnColor = btnColor.replace(/\{\{brand\.buttonTextColor\}\}/gi, data.buttonTextColor || '');
      btnColor = btnColor.replace(/\{brand\.buttonTextColor\}/gi, data.buttonTextColor || '');
      
      // Fallback to brand colors if still empty
      if (!btnBg) btnBg = data.buttonGenralColor || '#3cbec6';
      if (!btnColor) btnColor = data.buttonTextColor || '#ffffff';
      
      const btnRadius = toPx(data.borderRadius);
      return `<div style="text-align:${content.align};margin:20px 0;"><a href="${btnLink}" style="display:inline-block;padding:12px 30px;background-color:${btnBg};color:${btnColor};text-decoration:none;border-radius:${btnRadius};font-weight:600;">${btnText}</a></div>`;
    
    case 'divider':
      return `<div style="margin:20px 0;"><hr style="border:0;border-top:${content.height} solid ${content.color};" /></div>`;
    
    case 'spacer':
      return `<div style="height:${content.height};"></div>`;
    
    case 'hero':
      // Use creative data for hero section with placeholder support
      let heroImg = content.image || '';
      let heroHeadline = content.headline || '';
      let heroSubheadline = content.subheadline || '';
      let heroBtnText = content.buttonText || '';
      let heroBtnLink = content.buttonLink || '';
      
      // Replace placeholders
      heroImg = heroImg.replace(/\{\{bodyImage\}\}/gi, data.bodyImage || '');
      heroImg = heroImg.replace(/\{bodyImage\}/gi, data.bodyImage || '');
      heroHeadline = heroHeadline.replace(/\{\{headline\}\}/gi, data.headline || '');
      heroHeadline = heroHeadline.replace(/\{headline\}/gi, data.headline || '');
      heroSubheadline = heroSubheadline.replace(/\{\{body\}\}/gi, data.body || '');
      heroSubheadline = heroSubheadline.replace(/\{body\}/gi, data.body || '');
      heroSubheadline = heroSubheadline.replace(/\{\{additionalText\}\}/gi, data.additionalText || '');
      heroSubheadline = heroSubheadline.replace(/\{additionalText\}/gi, data.additionalText || '');
      heroBtnText = heroBtnText.replace(/\{\{bodyBtnName\}\}/gi, data.bodyBtnName || '');
      heroBtnText = heroBtnText.replace(/\{bodyBtnName\}/gi, data.bodyBtnName || '');
      heroBtnLink = heroBtnLink.replace(/\{\{bodyBtnLink\}\}/gi, data.bodyBtnLink || '');
      heroBtnLink = heroBtnLink.replace(/\{bodyBtnLink\}/gi, data.bodyBtnLink || '');
      
      // If still empty, use creative data as fallback
      if (!heroImg && data.bodyImage) heroImg = data.bodyImage;
      if (!heroHeadline && data.headline) heroHeadline = data.headline;
      if (!heroSubheadline && data.body) heroSubheadline = data.body;
      if (!heroBtnText && data.bodyBtnName) heroBtnText = data.bodyBtnName;
      if (!heroBtnLink && data.bodyBtnLink) heroBtnLink = data.bodyBtnLink;
      
      // Default values if still empty
      if (!heroHeadline) heroHeadline = 'Welcome!';
      if (!heroBtnLink) heroBtnLink = '#';
      
      return `<div style="text-align:center;padding:40px 20px;margin-bottom:20px;">
        ${heroImg ? `<img src="${heroImg}" alt="Hero" style="max-width:100%;height:auto;margin-bottom:25px;" />` : ''}
        <h1 style="margin:0 0 15px 0;font-size:36px;color:#333;font-weight:700;">${heroHeadline}</h1>
        <p style="font-size:18px;color:#666;margin:0 0 25px 0;">${heroSubheadline}</p>
        ${heroBtnText ? `<a href="${heroBtnLink}" style="display:inline-block;padding:14px 32px;background-color:${data.buttonGenralColor || '#3cbec6'};color:${data.buttonTextColor || '#ffffff'};text-decoration:none;border-radius:${toPx(data.borderRadius)};font-weight:600;">${heroBtnText}</a>` : ''}
      </div>`;
    
    case 'image-text':
      const itImg = content.image || data.bodyImage || '';
      const itHeading = content.heading || data.headline || 'Feature Title';
      const itText = content.text || data.body || '';
      
      return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;">
        <tr>
          <td width="45%" style="vertical-align:top;padding-right:20px;">
            ${itImg ? `<img src="${itImg}" alt="${itHeading}" style="max-width:100%;height:auto;" />` : ''}
          </td>
          <td width="55%" style="vertical-align:middle;">
            <h3 style="margin:0 0 12px 0;font-size:24px;color:#333;">${itHeading}</h3>
            <p style="margin:0;color:#666;line-height:1.6;">${itText}</p>
          </td>
        </tr>
      </table>`;
    
    case 'image-text-button':
      const itbImg = content.image || data.bodyImage || '';
      const itbHeading = content.heading || data.headline || 'Feature Title';
      const itbText = content.text || data.body || '';
      const itbBtnText = content.buttonText || data.bodyBtnName || '';
      const itbBtnLink = content.buttonLink || data.bodyBtnLink || '#';
      
      return `<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;">
        <tr>
          <td width="45%" style="vertical-align:top;padding-right:20px;">
            ${itbImg ? `<img src="${itbImg}" alt="${itbHeading}" style="max-width:100%;height:auto;" />` : ''}
          </td>
          <td width="55%" style="vertical-align:middle;">
            <h3 style="margin:0 0 12px 0;font-size:24px;color:#333;">${itbHeading}</h3>
            <p style="margin:0 0 18px 0;color:#666;line-height:1.6;">${itbText}</p>
            ${itbBtnText ? `<a href="${itbBtnLink}" style="display:inline-block;padding:12px 28px;background-color:${data.buttonGenralColor || '#3cbec6'};color:${data.buttonTextColor || '#ffffff'};text-decoration:none;border-radius:${toPx(data.borderRadius)};font-weight:600;">${itbBtnText}</a>` : ''}
          </td>
        </tr>
      </table>`;
    
    case 'cta':
      const ctaHeadline = content.headline || data.headline || 'Ready to start?';
      const ctaText = content.text || data.additionalText || data.body || '';
      const ctaBtnText = content.buttonText || data.bodyBtnName || '';
      const ctaBtnLink = content.buttonLink || data.bodyBtnLink || '#';
      
      return `<div style="text-align:center;padding:35px 25px;background-color:${content.bgColor};border-radius:8px;margin-bottom:20px;">
        <h2 style="margin:0 0 12px 0;color:#333;font-size:28px;">${ctaHeadline}</h2>
        <p style="margin:0 0 22px 0;color:#666;font-size:16px;">${ctaText}</p>
        ${ctaBtnText ? `<a href="${ctaBtnLink}" style="display:inline-block;padding:14px 32px;background-color:${data.buttonGenralColor || '#3cbec6'};color:${data.buttonTextColor || '#ffffff'};text-decoration:none;border-radius:${toPx(data.borderRadius)};font-weight:600;">${ctaBtnText}</a>` : ''}
      </div>`;
    
    case 'social':
      return `<div style="text-align:${content.align};margin:20px 0;">
        <p style="color:#999;font-size:14px;">Social Media Icons</p>
      </div>`;
    
    case 'video':
      const videoThumb = content.thumbnail || '';
      const videoUrl = content.videoUrl || '';
      return `<div style="text-align:center;margin-bottom:20px;">
        ${videoThumb ? `<a href="${videoUrl}"><img src="${videoThumb}" alt="${content.alt || 'Video'}" style="max-width:100%;height:auto;" /></a>` : `<p style="color:#999;">Video: ${videoUrl}</p>`}
      </div>`;
    
    case 'html':
      return content.code || '';
    
    default:
      return '';
  }
};

export { generateHTMLFromTemplate };
